import React, { useRef } from 'react';
import { useFramePositioning } from '../../hooks/useFramePositioning.js';
import { useOnDismiss } from '../../hooks/useOnDismiss.js';
import { cx } from '../../utils/cx';
import { RootPortal } from '../RootPortal';

export function ModalDialog({
    visible,
    primaryAction,
    primaryActionText,
    secondaryAction,
    secondaryActionText,
    icon,
    header,
    body,
    hideCloseButton,
    isDismissable,
    dismissableAction,
}) {
    const dialog = useRef(null);

    const {center} = useFramePositioning(dialog, visible);

    useOnDismiss(
        dialog,
        isDismissable && visible,
        dismissableAction || secondaryAction,
    );

    return (
        <RootPortal>
            <div
                className={cx(
                    'fixed z-10 inset-0 bg-black/30',
                    visible ? 'block' : 'hidden',
                )}
            >
                <div
                    className="bg-white w-[calc(90vw)] sm:w-[300px] p-6 flex flex-col items-center absolute left-1/2 -translate-x-1/2 max-w-content rounded-lg"
                    onClick={(event) => event.stopPropagation()}
                    ref={dialog}
                    style={{
                        top: center,
                    }}
                >
                    {!hideCloseButton &&
                        <button
                            className="absolute flex items-center justify-center w-10 h-10 right-0 top-0 rotate-45"
                            onClick={secondaryAction}
                        >
                            <i className="icon-plus block text-[16px]"></i>
                        </button>
                    }
                    <div className="flex flex-col items-center">
                        {icon &&
                            <div className="w-12 h-12 rounded-full mb-4 bg-primary flex items-center justify-center">
                                {SVGFactory(icon)}
                            </div>
                        }
                        <div className="mb-0.5 font-bold text-base text-center">
                            {header}
                        </div>
                    </div>
                    <div className="w-0 min-w-full">
                        <p className="text-sm text-center">{body}</p>
                    </div>
                    <div className="flex w-full flex-col items-center mt-4 gap-3">
                        {primaryActionText &&
                            <button
                                className="flex w-full items-center justify-center gap-x-2 rounded-full px-6 py-3 text-sm font-bold border border-primary bg-primary text-primarytext"
                                onClick={primaryAction}
                            >
                                {primaryActionText}
                            </button>
                        }

                        {secondaryActionText &&
                            <button
                                className="flex w-full items-center justify-center gap-x-2 rounded-full px-6 py-3 text-sm font-bold border border-primary"
                                onClick={secondaryAction}
                            >
                                {secondaryActionText}
                            </button>
                        }
                    </div>
                </div>
            </div>
        </RootPortal>
    );
};

const SVGFactory = (type) => {
    if (type === 'cart') {
        return (
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <rect width="64" height="64" rx="32" fill="#F2AB49" />
                <g filter="url(#filter0_d_3066_4322)">
                    <path
                        d="M43.9875 24.175L42.4625 32.5375C42.3367 33.2283 41.9726 33.8531 41.4335 34.3031C40.8945 34.753 40.2147 34.9997 39.5125 35H25.0125L25.5625 38H39C39.5933 38 40.1734 38.1759 40.6667 38.5056C41.1601 38.8352 41.5446 39.3038 41.7716 39.8519C41.9987 40.4001 42.0581 41.0033 41.9424 41.5853C41.8266 42.1672 41.5409 42.7018 41.1213 43.1213C40.7018 43.5409 40.1672 43.8266 39.5853 43.9424C39.0033 44.0581 38.4001 43.9987 37.852 43.7716C37.3038 43.5446 36.8352 43.1601 36.5056 42.6667C36.1759 42.1734 36 41.5933 36 41C36.0001 40.6591 36.0593 40.3207 36.175 40H28.825C28.9407 40.3207 28.9999 40.6591 29 41C29.0009 41.484 28.8847 41.9611 28.6612 42.3905C28.4378 42.8198 28.1138 43.1888 27.7169 43.4658C27.3199 43.7428 26.8619 43.9196 26.3818 43.9812C25.9017 44.0428 25.4138 43.9873 24.9598 43.8195C24.5058 43.6517 24.0991 43.3765 23.7746 43.0174C23.45 42.6583 23.2171 42.2261 23.0958 41.7575C22.9744 41.2889 22.9683 40.7979 23.0779 40.3265C23.1875 39.855 23.4095 39.4171 23.725 39.05L20.2625 20H18C17.7348 20 17.4804 19.8946 17.2929 19.7071C17.1054 19.5196 17 19.2652 17 19C17 18.7348 17.1054 18.4804 17.2929 18.2929C17.4804 18.1054 17.7348 18 18 18H20.2625C20.7293 18.001 21.181 18.1653 21.5395 18.4644C21.8979 18.7634 22.1404 19.1784 22.225 19.6375L22.8375 23H43C43.146 23.0003 43.2901 23.033 43.4219 23.0957C43.5538 23.1583 43.6701 23.2495 43.7625 23.3625C43.8592 23.472 43.9297 23.602 43.9687 23.7428C44.0077 23.8836 44.0141 24.0314 43.9875 24.175Z"
                        fill="white"
                    />
                </g>
                <defs>
                    <filter
                        id="filter0_d_3066_4322"
                        x="17"
                        y="18"
                        width="27.0034"
                        height="27.0056"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                    >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                        />
                        <feOffset dy="1" />
                        <feComposite in2="hardAlpha" operator="out" />
                        <feColorMatrix
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0"
                        />
                        <feBlend
                            mode="normal"
                            in2="BackgroundImageFix"
                            result="effect1_dropShadow_3066_4322"
                        />
                        <feBlend
                            mode="normal"
                            in="SourceGraphic"
                            in2="effect1_dropShadow_3066_4322"
                            result="shape"
                        />
                    </filter>
                </defs>
            </svg>
        );
    }

    if (type === 'close') {
        return (
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <g clipPath="url(#clip0_3066_4374)">
                    <path
                        d="M17.2533 15.9999L24.6221 8.63106C24.7678 8.46101 24.8439 8.24228 24.8352 8.01857C24.8266 7.79485 24.7338 7.58264 24.5755 7.42434C24.4172 7.26603 24.205 7.17329 23.9813 7.16465C23.7576 7.15601 23.5389 7.2321 23.3688 7.37773L15.9999 14.7466L8.63103 7.36884C8.46365 7.20146 8.23663 7.10742 7.99992 7.10742C7.76321 7.10742 7.53619 7.20146 7.36881 7.36884C7.20143 7.53622 7.1074 7.76324 7.1074 7.99995C7.1074 8.23666 7.20143 8.46368 7.36881 8.63106L14.7466 15.9999L7.36881 23.3688C7.27576 23.4485 7.20019 23.5466 7.14683 23.6569C7.09348 23.7671 7.0635 23.8873 7.05877 24.0097C7.05404 24.1321 7.07467 24.2542 7.11936 24.3682C7.16404 24.4823 7.23183 24.5859 7.31846 24.6725C7.40508 24.7592 7.50868 24.8269 7.62275 24.8716C7.73681 24.9163 7.85889 24.9369 7.9813 24.9322C8.10372 24.9275 8.22384 24.8975 8.33412 24.8441C8.4444 24.7908 8.54246 24.7152 8.62214 24.6222L15.9999 17.2533L23.3688 24.6222C23.5389 24.7678 23.7576 24.8439 23.9813 24.8352C24.205 24.8266 24.4172 24.7339 24.5755 24.5756C24.7338 24.4173 24.8266 24.205 24.8352 23.9813C24.8439 23.7576 24.7678 23.5389 24.6221 23.3688L17.2533 15.9999Z"
                        fill="black"
                    />
                </g>
                <defs>
                    <clipPath id="clip0_3066_4374">
                        <rect width="32" height="32" fill="white" />
                    </clipPath>
                </defs>
            </svg>
        );
    }

    if (type === 'exclamation') {
        return (
            <svg
                width="6"
                height="25"
                viewBox="0 0 6 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <g filter="url(#filter0_d_5836_5193)">
                    <path
                        d="M0.16 5.664L0.736 16.896H4.768L5.312 5.664V0H0.16V5.664ZM5.504 21.056C5.504 19.424 4.576 18.56 2.752 18.56C0.896 18.56 0 19.424 0 21.056C0 22.72 0.896 23.552 2.752 23.552C4.608 23.552 5.504 22.688 5.504 21.056Z"
                        fill="white"
                    />
                </g>
                <defs>
                    <filter
                        id="filter0_d_5836_5193"
                        x="0"
                        y="0"
                        width="5.50391"
                        height="24.552"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                    >
                        <feFlood
                            floodOpacity="0"
                            result="BackgroundImageFix"
                        />
                        <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                        />
                        <feOffset dy="1" />
                        <feComposite in2="hardAlpha" operator="out" />
                        <feColorMatrix
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0"
                        />
                        <feBlend
                            mode="normal"
                            in2="BackgroundImageFix"
                            result="effect1_dropShadow_5836_5193"
                        />
                        <feBlend
                            mode="normal"
                            in="SourceGraphic"
                            in2="effect1_dropShadow_5836_5193"
                            result="shape"
                        />
                    </filter>
                </defs>
            </svg>
        );
    }
    if (type === 'leave') {
        return (
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <rect width="64" height="64" rx="32" fill="#F2AB49" />
                <g filter="url(#filter0_d_3066_4366)">
                    <g filter="url(#filter1_d_3066_4366)">
                        <path
                            d="M30.2132 26.8933C31.4532 26.8933 32.4665 25.8666 32.4665 24.6266C32.4665 24.029 32.2291 23.4558 31.8065 23.0332C31.3839 22.6107 30.8108 22.3733 30.2132 22.3733C28.9598 22.3733 27.9465 23.3866 27.9465 24.6266C27.9465 25.8666 28.9598 26.8933 30.2132 26.8933ZM34.2665 42.5733L42.1732 40.9999L41.7198 38.7333L36.1865 39.8666L34.3865 30.7199L36.4132 31.5199V35.3333H38.6665V30.0533L32.7998 27.5599L31.9065 27.4533C31.1065 27.4533 30.4398 27.9066 29.9998 28.5866L28.8532 30.3866C27.9465 31.9999 26.1465 33.0933 23.9998 33.0933V35.3333C26.4798 35.3333 28.7465 34.2266 30.2132 32.5333L30.8798 35.9199L28.5198 38.1733V46.6666H30.7732V39.8666L33.1465 37.6133L34.2665 42.5733ZM19.9998 46.6666H22.6665V19.9999H39.9998V37.4799L42.6665 36.9199V17.3333H19.9998V46.6666ZM39.9998 46.6666H42.6665V42.3733L39.9998 42.9333V46.6666Z"
                            fill="white"
                        />
                    </g>
                </g>
                <defs>
                    <filter
                        id="filter0_d_3066_4366"
                        x="16"
                        y="16"
                        width="32"
                        height="33"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                    >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                        />
                        <feOffset dy="1" />
                        <feComposite in2="hardAlpha" operator="out" />
                        <feColorMatrix
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0"
                        />
                        <feBlend
                            mode="normal"
                            in2="BackgroundImageFix"
                            result="effect1_dropShadow_3066_4366"
                        />
                        <feBlend
                            mode="normal"
                            in="SourceGraphic"
                            in2="effect1_dropShadow_3066_4366"
                            result="shape"
                        />
                    </filter>
                    <filter
                        id="filter1_d_3066_4366"
                        x="20"
                        y="17.3333"
                        width="22.6665"
                        height="30.3333"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                    >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                        />
                        <feOffset dy="1" />
                        <feComposite in2="hardAlpha" operator="out" />
                        <feColorMatrix
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0"
                        />
                        <feBlend
                            mode="normal"
                            in2="BackgroundImageFix"
                            result="effect1_dropShadow_3066_4366"
                        />
                        <feBlend
                            mode="normal"
                            in="SourceGraphic"
                            in2="effect1_dropShadow_3066_4366"
                            result="shape"
                        />
                    </filter>
                </defs>
            </svg>
        );
    }
    if (type === 'clock') {
        return (
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <g filter="url(#filter0_d_3066_4395)">
                    <path
                        d="M15.9999 2.28564C23.5736 2.28564 29.7142 8.42622 29.7142 15.9999C29.7142 23.5736 23.5736 29.7142 15.9999 29.7142C8.42622 29.7142 2.28564 23.5736 2.28564 15.9999C2.28564 8.42622 8.42622 2.28564 15.9999 2.28564ZM15.9999 4.19079C9.48793 4.19079 4.19079 9.48793 4.19079 15.9999C4.19079 22.5119 9.48793 27.8091 15.9999 27.8091C22.5119 27.8091 27.8091 22.5119 27.8091 15.9999C27.8091 9.48793 22.5119 4.19079 15.9999 4.19079ZM15.1428 7.99993C15.3499 7.99994 15.55 8.07495 15.7061 8.21109C15.8622 8.34724 15.9638 8.5353 15.9919 8.7405L15.9999 8.85707V15.9999H20.8571C21.0742 16 21.2833 16.0825 21.442 16.2308C21.6007 16.379 21.6972 16.582 21.712 16.7987C21.7268 17.0153 21.6588 17.2295 21.5217 17.398C21.3847 17.5665 21.1888 17.6766 20.9736 17.7062L20.8571 17.7142H15.1428C14.9357 17.7142 14.7355 17.6392 14.5794 17.5031C14.4233 17.3669 14.3218 17.1788 14.2936 16.9736L14.2856 16.8571V8.85707C14.2856 8.62975 14.376 8.41173 14.5367 8.25098C14.6974 8.09024 14.9155 7.99993 15.1428 7.99993Z"
                        fill="white"
                    />
                </g>
                <defs>
                    <filter
                        id="filter0_d_3066_4395"
                        x="2.28564"
                        y="2.28564"
                        width="27.4287"
                        height="28.4285"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                    >
                        <feFlood
                            floodOpacity="0"
                            result="BackgroundImageFix"
                        />
                        <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                        />
                        <feOffset dy="1" />
                        <feComposite in2="hardAlpha" operator="out" />
                        <feColorMatrix
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0"
                        />
                        <feBlend
                            mode="normal"
                            in2="BackgroundImageFix"
                            result="effect1_dropShadow_3066_4395"
                        />
                        <feBlend
                            mode="normal"
                            in="SourceGraphic"
                            in2="effect1_dropShadow_3066_4395"
                            result="shape"
                        />
                    </filter>
                </defs>
            </svg>
        );
    }

    if (type === 'question') {
        return (
            <svg
                width="72"
                height="72"
                viewBox="0 0 72 72"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <circle cx="36" cy="36" r="36" fill="#D23C2E" />
                <path
                    d="M31.544 34.904V40.728H36.376V37.784C40.984 37.24 43.32 34.488 43.32 30.424C43.32 25.272 39.832 23.832 35.576 23.832C33.08 23.832 30.776 24.408 28.6 25.24V31.128L32.504 30.84L32.792 28.792C32.856 28.344 32.888 28.28 33.208 28.152C33.752 27.928 34.424 27.768 35.352 27.768C36.984 27.768 37.944 28.76 37.944 30.584C37.944 34.296 34.328 34.68 31.544 34.904ZM36.696 44.888C36.696 43.256 35.768 42.392 33.976 42.392C32.12 42.392 31.224 43.256 31.224 44.888C31.224 46.552 32.12 47.384 33.976 47.384C35.8 47.384 36.696 46.52 36.696 44.888Z"
                    fill="white"
                />
            </svg>
        );
    }
};
